import { Heading } from 'react-aria-components';
import { useTranslation } from 'react-i18next';
import { AriaModalDialog } from '@/shared/a11y/AriaDialog';
import { Button } from '@/components/ui/button';

/**
 * The Delete confirmation (R9.9): a soft delete, restorable by a
 * System_Administrator within the configured retention period. A plain
 * yes/no confirmation — there is no reason to capture, unlike Cancel.
 *
 * A true dialog (focus trap, focus restore, `Esc`) via {@link AriaModalDialog}.
 */
export function DeleteInitiativeDialog({
  isOpen,
  isSubmitting,
  hasError,
  onConfirm,
  onClose,
}: {
  readonly isOpen: boolean;
  readonly isSubmitting: boolean;
  readonly hasError: boolean;
  readonly onConfirm: () => void;
  readonly onClose: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <AriaModalDialog
      variant="centre"
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      aria-label={t('detail.deleteDialog.title')}
    >
      <Heading slot="title" className="text-[15px] font-semibold text-txt">
        {t('detail.deleteDialog.title')}
      </Heading>
      <p className="text-muted-foreground mt-2 text-[12.3px]">{t('detail.deleteDialog.body')}</p>

      {hasError ? (
        <p className="mt-2 text-[12px] text-[#8E2A1E]">{t('detail.deleteDialog.error')}</p>
      ) : null}

      <div className="mt-4 flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onClose} disabled={isSubmitting}>
          {t('detail.deleteDialog.cancel')}
        </Button>
        <Button variant="destructive" size="sm" disabled={isSubmitting} onClick={onConfirm}>
          {isSubmitting ? t('detail.deleteDialog.confirming') : t('detail.deleteDialog.confirm')}
        </Button>
      </div>
    </AriaModalDialog>
  );
}
